import React, { useState, useEffect } from 'react';
import { Upload, Image as ImageIcon, Folder, Tag, Search, X, Loader2 } from 'lucide-react';
import { apiClient } from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

export default function MediaLibrary() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState({ type: '', folder: '', tags: '', search: '' });
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  useEffect(() => {
    loadMedia();
  }, [filter]);

  const loadMedia = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter.type) params.append('type', filter.type);
      if (filter.folder) params.append('folder', filter.folder);
      if (filter.tags) params.append('tags', filter.tags);

      const response = await apiClient.get(`/api/media/?${params.toString()}`);
      
      // Filter by search locally
      let filteredMedia = response;
      if (filter.search) {
        const searchLower = filter.search.toLowerCase();
        filteredMedia = filteredMedia.filter(m => 
          m.title?.toLowerCase().includes(searchLower) ||
          m.alt_text?.toLowerCase().includes(searchLower) ||
          m.tags?.some(t => t.toLowerCase().includes(searchLower))
        );
      }
      
      setMedia(filteredMedia);
    } catch (error) {
      console.error('Failed to load media:', error);
      toast.error('Грешка при зареждане на медията');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const file = formData.get('file');

    if (!file || file.size === 0) {
      toast.error('Моля изберете файл');
      return;
    }

    try {
      setUploading(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('alt_text', formData.get('alt_text') || '');
      uploadFormData.append('title', formData.get('title') || file.name);
      uploadFormData.append('caption', formData.get('caption') || '');
      uploadFormData.append('tags', formData.get('tags') || '');
      uploadFormData.append('folder', formData.get('folder') || '');

      const response = await apiClient.post('/api/media/upload', uploadFormData);

      if (response.success) {
        toast.success(response.duplicate ? 'Този файл вече съществува' : 'Файлът е качен успешно');
        setShowUploadDialog(false);
        loadMedia();
        e.target.reset();
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Грешка при качване на файл');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateMetadata = async (mediaId, updates) => {
    try {
      await apiClient.patch(`/api/media/${mediaId}`, updates);
      toast.success('Метаданните са обновени');
      loadMedia();
      setSelectedMedia(null);
    } catch (error) {
      console.error('Update failed:', error);
      toast.error('Грешка при обновяване');
    }
  };

  const handleArchive = async (mediaId) => {
    try {
      await apiClient.post(`/api/media/${mediaId}/archive`, {});
      toast.success('Медията е архивирана');
      loadMedia();
      setSelectedMedia(null);
    } catch (error) {
      console.error('Archive failed:', error);
      toast.error('Грешка при архивиране');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-playfair font-bold">Media Library</h1>
          <p className="text-neutral-400 mt-1">Управление на изображения и видеа</p>
        </div>
        <Button 
          onClick={() => setShowUploadDialog(true)}
          className="bg-accent-gold hover:bg-accent-gold-dark"
        >
          <Upload className="w-4 h-4 mr-2" />
          Качи файл
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Търсене..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            className="pl-10"
          />
        </div>
        <select
          value={filter.type}
          onChange={(e) => setFilter({ ...filter, type: e.target.value })}
          className="px-4 py-2 bg-primary-charcoal border border-neutral-700 rounded-lg text-sm"
        >
          <option value="">Всички типове</option>
          <option value="image">Изображения</option>
          <option value="video_embed">Видео</option>
        </select>
        <Input
          placeholder="Филтър по папка"
          value={filter.folder}
          onChange={(e) => setFilter({ ...filter, folder: e.target.value })}
          className="w-40"
        />
        {(filter.type || filter.folder || filter.tags) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilter({ type: '', folder: '', tags: '', search: filter.search })}
          >
            <X className="w-4 h-4 mr-1" />
            Изчисти
          </Button>
        )}
      </div>

      {/* Media Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-accent-gold" />
        </div>
      ) : media.length === 0 ? (
        <div className="text-center py-20">
          <ImageIcon className="w-16 h-16 mx-auto mb-4 text-neutral-600" />
          <p className="text-neutral-400">Няма намерени файлове</p>
          <Button 
            onClick={() => setShowUploadDialog(true)}
            variant="link"
            className="mt-2 text-accent-gold"
          >
            Качете първия си файл
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {media.map((item) => (
            <div
              key={item._id}
              onClick={() => setSelectedMedia(item)}
              className="group relative aspect-square bg-primary-charcoal rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-accent-gold transition-all"
            >
              {item.type === 'image' ? (
                <img
                  src={item.url_small || item.url_thumbnail || item.url_original}
                  alt={item.alt_text || item.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-primary-charcoal">
                  <ImageIcon className="w-12 h-12 text-neutral-600" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end">
                <div className="p-3 w-full">
                  <p className="text-sm font-medium truncate">{item.title}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-neutral-300">
                    {item.folder && (
                      <span className="flex items-center gap-1">
                        <Folder className="w-3 h-3" />
                        {item.folder}
                      </span>
                    )}
                    {item.tags?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        {item.tags.length}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="bg-primary-charcoal border-neutral-700 max-w-lg">
          <DialogHeader>
            <DialogTitle>Качване на файл</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Файл *</label>
              <Input
                type="file"
                name="file"
                accept="image/jpeg,image/png,image/webp"
                required
                className="cursor-pointer"
              />
              <p className="text-xs text-neutral-400 mt-1">JPG, PNG, WebP до 12MB</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Заглавие</label>
              <Input name="title" placeholder="Име на файла" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Alt текст</label>
              <Input name="alt_text" placeholder="Описание за достъпност" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Описание</label>
              <Input name="caption" placeholder="Кратко описание" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Папка</label>
              <Input name="folder" placeholder="properties, team, hero..." />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Тагове</label>
              <Input name="tags" placeholder="luxury, modern, sofia (разделени със запетая)" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowUploadDialog(false)}
                disabled={uploading}
                className="flex-1"
              >
                Откажи
              </Button>
              <Button
                type="submit"
                disabled={uploading}
                className="flex-1 bg-accent-gold hover:bg-accent-gold-dark"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Качване...
                  </>
                ) : (
                  'Качи'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Media Details Dialog */}
      {selectedMedia && (
        <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
          <DialogContent className="bg-primary-charcoal border-neutral-700 max-w-3xl">
            <DialogHeader>
              <DialogTitle>Детайли на файла</DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              {/* Image Preview */}
              {selectedMedia.type === 'image' && (
                <div className="aspect-video bg-primary-black rounded-lg overflow-hidden">
                  <img
                    src={selectedMedia.url_medium || selectedMedia.url_original}
                    alt={selectedMedia.alt_text || selectedMedia.title}
                    className="w-full h-full object-contain"
                  />
                </div>
              )}

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-neutral-400">Име</p>
                  <p className="font-medium">{selectedMedia.title}</p>
                </div>
                <div>
                  <p className="text-neutral-400">Размер</p>
                  <p className="font-medium">
                    {selectedMedia.width} × {selectedMedia.height}px
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-neutral-400">URL</p>
                  <p className="font-mono text-xs break-all">{selectedMedia.url_original}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-neutral-700">
                <Button
                  variant="outline"
                  onClick={() => setSelectedMedia(null)}
                  className="flex-1"
                >
                  Затвори
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleArchive(selectedMedia._id)}
                  className="flex-1"
                >
                  Архивирай
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
